import { InputAdornment } from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import React, { useEffect, useState } from "react";
import {
  ContractDetailField,
  DetailFieldType,
  RecordsDetail,
} from "@features/projects-feature/projectTypes";
import {
  ContractDto,
  ServiceRequestPicklist,
  ServiceReportsFilterRequest,
  ServiceRequestQueryType,
} from "@lib/ShiOneClient";
import { useGetProjects } from "@features/projects-feature/hooks/useGetProjects";
import { useGetElasticServiceRequests } from "@features/service-requests-feature/hooks/useServiceRequests";
import { useGetContractInvoices } from "@features/projects-feature/hooks/useGetContractInvoices";
export const SearchParamAccessors = {
  contractName: "contractName",
  defaultView: "defaultView",
};

export const ContractDetailFields: ContractDetailField[] = [
  {
    label: "Contract",
    fieldKey: "contractName",
    inputProps: {
      readOnly: true,
    },
    type: DetailFieldType.Text,
    disabled: true,
    columnKey: 1,
    sortPriority: 1,
  },
  {
    label: "Contract Type",
    fieldKey: "contractServiceType",
    inputProps: {
      readOnly: true,
    },
    type: DetailFieldType.Text,
    columnKey: 1,
    sortPriority: 2,
  },
  {
    label: "Contract Service Level",
    fieldKey: "serviceLevelAgreementID",
    inputProps: {
      readOnly: true,
    },
    type: DetailFieldType.Text,
    columnKey: 1,
    sortPriority: 3,
    isPicklist: true,
    picklistEntityKey: "contract",
    picklistFieldKey: "ServiceLevelAgreementID",
  },
  {
    label: "Start Date",
    fieldKey: "startDate",
    inputProps: {
      readOnly: true,
      endAdornment: (
        <InputAdornment position="end">
          <DateRangeIcon sx={{ color: "action.active" }} />
        </InputAdornment>
      ),
    },
    type: DetailFieldType.Date,
    columnKey: 1,
    sortPriority: 4,
  },
  {
    label: "Contract Vendor",
    fieldKey: "contractVendor",
    inputProps: {
      readOnly: true,
    },
    type: DetailFieldType.Text,
    disabled: true,
    columnKey: 2,
    sortPriority: 1,
  },
  {
    label: "Contract Sub Type",
    fieldKey: "contractSubType",
    inputProps: {
      readOnly: true,
    },
    type: DetailFieldType.Text,
    columnKey: 2,
    sortPriority: 2,
  },
  {
    label: "Billing Type",
    fieldKey: "contractType",
    inputProps: {
      readOnly: true,
    },
    type: DetailFieldType.Text,
    columnKey: 2,
    sortPriority: 3,
    isPicklist: true,
    picklistEntityKey: "contract",
    picklistFieldKey: "ContractType",
  },
  {
    label: "End Date",
    fieldKey: "endDate",
    inputProps: {
      readOnly: true,
      endAdornment: (
        <InputAdornment position="end">
          <DateRangeIcon sx={{ color: "action.active" }} />
        </InputAdornment>
      ),
    },
    type: DetailFieldType.Date,
    columnKey: 2,
    sortPriority: 4,
  },
];

export const useContractRecordsDetailFieldsForContracts = (
  contract: ContractDto,
): { loading: boolean; data: RecordsDetail[] } => {
  const [contractProjectsCount, setContractProjectsCount] = useState(0);
  const [contractInvoicesCount, setContractInvoicesCount] = useState(0);

  const {
    data: projectsResponse,
    isLoading: projectsLoading,
    isError: projectsError,
  } = useGetProjects();

  const {
    data: contractInvoices,
    isLoading: contractInvoicesLoading,
    isError: contractInvoicesError,
  } = useGetContractInvoices(contract?.id!);

  const loading = projectsLoading || contractInvoicesLoading;

  const serviceRequestQuery = new ServiceReportsFilterRequest({
    page: 0,
    pageSize: 1,
    sortByColumn: "CreatedDate",
    isDescending: true,
    searchFilterInclude: ServiceRequestPicklist.fromJS({
      contractName: [contract?.contractName],
    }),
    queryType: ServiceRequestQueryType.AllRequests,
  });

  const { data: serviceRequestsResponse } =
    useGetElasticServiceRequests(serviceRequestQuery);

  useEffect(() => {
    if (projectsResponse && !projectsError && !projectsLoading) {
      const projects =
        projectsResponse.projects && projectsResponse.projects.length > 0
          ? projectsResponse.projects.filter(
              (proj) => proj.contractID === contract?.id,
            )
          : [];
      setContractProjectsCount(projects.length);
    }
  }, [projectsResponse, projectsLoading, projectsError, contract?.id]);

  useEffect(() => {
    if (
      contractInvoices &&
      !contractInvoicesLoading &&
      !contractInvoicesError
    ) {
      if (contractInvoices.length > 0) {
        setContractInvoicesCount(contractInvoices.length);
      } else {
        setContractInvoicesCount(0);
      }
    }
  }, [contractInvoices, contractInvoicesLoading, contractInvoicesError]);

  return {
    loading,
    data: [
      {
        title: "Requests",
        count: serviceRequestsResponse?.totalCount ?? 0,
        linkTo: `/support-center/requests?filterInclude=contractName:`,
        contractName: contract?.contractName ?? "",
      },
      {
        title: "Service Engagements",
        count: contractProjectsCount,
        linkTo: `/services/service-engagements?filterInclude=contractName:`,
        contractName: contract?.contractName ?? "",
      },
      {
        title: "Invoices",
        count: contractInvoicesCount,
        linkTo: `/services/billing?${SearchParamAccessors.contractName}=`,
        contractName: contract?.contractName ?? "",
      },
    ],
  };
};

export const useContractRecordsDetailFields =
  useContractRecordsDetailFieldsForContracts;
